import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  IconButton,
  Paper,
  CircularProgress
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { verificationCode, queryAccountByCouponCode } from '../../api/modules/invite';    
import { showMessage } from '../../utils';
import { useNavigate } from 'react-router-dom';
import AccountSelectDialog from './AccountSelectDialog';
import VerificationResult from './VerificationResult';
import { isInApp } from '../../utils/environment';

const EnterCode = ({ onClose }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState(null);
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const navigate = useNavigate();

  const handleCodeChange = (event) => {
    const value = event.target.value;
    // 允许输入任何字符，限制长度为12
    if (value.length <= 12) {
      setCode(value);
      setError('');
    }
  };

  const handleVerify = async () => {
    if (code.length < 6) {
      setError('Please enter at least 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await queryAccountByCouponCode({code}, (rst) => {
        const { code: responseCode, data, msg } = rst;
        
        if (responseCode === 200) {
          if (data && data.length > 0) {
            setAccounts(data);
            setShowAccountDialog(true);
          } else {
            showMessage('No valid account found for this code', 'error');
          }
        } else {
          showMessage(msg || 'Verification failed', 'error');
        }
      });
    } catch (error) {   
      showMessage('Verification failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelect = (account) => {
    setShowAccountDialog(false);
    navigate('/campaigns/invitations/verification-result', { 
      state: { 
        account,
        code,
        useType: 'Enter Code' 
      } 
    });
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'background.paper',
        zIndex: 1300,
      }}
    >
      {/* Header */}
      {
        !isInApp() && (
          <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
          <IconButton onClick={() => navigate('/campaigns/invitations/verification-code')} edge="start" sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            Enter Code
          </Typography>
        </Box>
        )
      }

      {/* Content */}
      <Box sx={{ p: 3 }}>
        <Paper elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 3 }}>
            Enter Verification Code
          </Typography>

          <TextField
            fullWidth
            placeholder="Enter 6-12 digit code"
            value={code}
            onChange={handleCodeChange}
            error={!!error}
            helperText={error}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#F8F9FA',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.12)'
                }
              }
            }}
          />

          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 3 }}>
            Please enter the verification code shown by the customer
          </Typography>

          <Button
            fullWidth
            variant="contained"
            onClick={handleVerify}
            disabled={code.length < 6 || loading}
            sx={{
              bgcolor: code.length >= 6 ? 'black' : '#E2E8F0',
              color: 'white',
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontSize: '1rem',
              '&:hover': {
                bgcolor: code.length >= 6 ? '#333' : '#E2E8F0'
              },
              '&.Mui-disabled': {
                bgcolor: '#E2E8F0',
                color: 'rgba(0, 0, 0, 0.26)'
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Query Account'}
          </Button>
        </Paper>
      </Box>

      <AccountSelectDialog
        open={showAccountDialog}
        accounts={accounts}
        onClose={() => setShowAccountDialog(false)}
        onSelect={handleAccountSelect}
      />
    </Box>
  );
};

export default EnterCode;